import React from "react"
import { StyleSheet } from "react-native"

import type { VideoViewProps } from "expo-video"
import Hls from "hls.js"

import { getSourceUri, isHlsUrl } from "src/utils/mediaTypes"

/**
 * Web implementation of HlsVideoView.
 *
 * `expo-video`'s built-in `VideoView` on web is a thin wrapper around the
 * native HTML5 `<video>` element that simply sets `<video src="…">`. Chrome
 * and Firefox have no native HLS support, so when the source is a `.m3u8`
 * playlist they cannot seek into a specific segment — they just walk through
 * every `.ts` segment in playlist order to reach the requested time.
 *
 * This component renders its own `<video>` element and:
 *   1) On Safari (which has native HLS), sets `<video src="…">` directly so
 *      the OS-level player handles seeking.
 *   2) Everywhere else, attaches an `hls.js` instance to the element via
 *      Media Source Extensions. `hls.js` uses byte-range / segment-aware
 *      requests so a seek skips straight to the relevant segment instead of
 *      downloading everything before it.
 *
 * The element is registered with the `expo-video` web `VideoPlayer` via
 * `mountVideoView`, so all of the existing play/pause/seek/`isPlaying`/
 * `currentTime` plumbing in screens like `video-player.tsx` keeps working
 * unchanged — `expo-video`'s player class wires up DOM listeners on the
 * mounted element, regardless of who set up its media source.
 */

export interface HlsVideoViewRef {
  enterFullscreen: () => Promise<void>
  exitFullscreen: () => Promise<void>
  startPictureInPicture: () => Promise<void>
  stopPictureInPicture: () => Promise<void>
}

// Internal duck-typed view of the web VideoPlayer. We can't import the class
// directly because it's only available behind the platform extension, but the
// public `VideoPlayer` interface deliberately omits these web-only details
// (mountVideoView, src). Keeping this local avoids leaking that into callers.
interface VideoPlayerWebInternals {
  src: unknown
  mountVideoView: (video: HTMLVideoElement) => void
  unmountVideoView: (video: HTMLVideoElement) => void
}

export type HlsVideoViewProps = VideoViewProps

export const HlsVideoView = React.forwardRef<HlsVideoViewRef, HlsVideoViewProps>(
  function HlsVideoView(props, ref) {
    const videoRef = React.useRef<HTMLVideoElement>(null)
    const hlsRef = React.useRef<Hls | null>(null)

    React.useImperativeHandle(ref, () => ({
      enterFullscreen: async () => {
        if (!props.allowsFullscreen) return
        await videoRef.current?.requestFullscreen()
      },
      exitFullscreen: async () => {
        await document.exitFullscreen()
      },
      startPictureInPicture: async () => {
        await videoRef.current?.requestPictureInPicture()
      },
      stopPictureInPicture: async () => {
        try {
          await document.exitPictureInPicture()
        } catch (e) {
          if (e instanceof DOMException && e.name === "InvalidStateError") {
            console.warn("The HlsVideoView is not in Picture-in-Picture mode.")
          } else {
            throw e
          }
        }
      },
    }))

    // Mount the underlying <video> element with the expo-video player so all
    // play/pause/seek/state plumbing keeps flowing through the existing
    // VideoPlayerWeb instance.
    React.useEffect(() => {
      const video = videoRef.current
      if (!video) return
      const player = props.player as unknown as VideoPlayerWebInternals
      player.mountVideoView(video)
      return () => {
        player.unmountVideoView(video)
      }
    }, [props.player])

    // (Re-)attach the source whenever the player's source changes. On Safari we
    // can let native HLS handle it; everywhere else we hand the playlist to
    // hls.js so seeks request the right segment directly.
    React.useEffect(() => {
      const video = videoRef.current
      if (!video) return

      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }

      const player = props.player as unknown as VideoPlayerWebInternals
      const uri = getSourceUri(player.src as Parameters<typeof getSourceUri>[0])
      if (!uri) {
        video.removeAttribute("src")
        video.load()
        return
      }

      const isHls = isHlsUrl(uri)
      // `canPlayType` is unreliable for HLS — Chrome returns "maybe" for
      // `application/vnd.apple.mpegurl` even though it has no native HLS
      // support. Prefer hls.js (MSE-backed) whenever it's available so all
      // browsers get consistent seek-to-segment behavior. Only fall back to
      // a native `<video src>` assignment when hls.js can't run (very old
      // browsers / no MSE), which also covers Safari's own native HLS path.
      if (isHls && Hls.isSupported()) {
        const hls = new Hls({
          // Cap the back buffer so seeking around doesn't grow memory
          // unboundedly for long videos (default is unlimited).
          backBufferLength: 30,
        })
        hlsRef.current = hls
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            console.error("[HlsVideoView] fatal hls.js error", data.type, data.details)
          }
        })
        hls.loadSource(uri)
        hls.attachMedia(video)
        return () => {
          hls.destroy()
          if (hlsRef.current === hls) hlsRef.current = null
        }
      }

      if (video.src !== uri) {
        video.src = uri
        video.load()
      }
      return undefined
      // We intentionally re-run this effect when the underlying player or its
      // src reference changes. `useVideoPlayer` produces a new player instance
      // when the source string changes (it keys on JSON.stringify(source)), so
      // depending on `props.player` is sufficient to catch source swaps.
    }, [props.player])

    const flatStyle = StyleSheet.flatten(props.style)

    return (
      <video
        ref={videoRef}
        controls={props.nativeControls ?? true}
        controlsList={props.allowsFullscreen ? undefined : "nofullscreen"}
        crossOrigin={props.crossOrigin}
        style={{ ...flatStyle, objectFit: props.contentFit } as React.CSSProperties}
        disablePictureInPicture={!props.allowsPictureInPicture}
        playsInline={props.playsInline}
      />
    )
  },
)
